import React, { useState, useRef, useCallback } from 'react'
import SearchBar, { SearchBarProps, SearchButton } from './SearchBar'
import HeadToHead from 'components/head-to-head/H2HModal'
import { useBooleanState } from 'hooks/use-states'
import i18n from 'constants/strings'
import styles from './styles/search-bar.module.scss'

import { connect, Omit, useSelector } from 'react-redux'
import { suggestPlayers, clearSuggestions } from 'actions/search-actions'
import { State } from 'reducers/search-reducer'
import MainContainer from 'components/generic/MainContainer'
import { MIN_SEARCH_CHARS } from 'constants/global'
import PlayerSuggestion from './PlayerSuggestion'
import { AppState } from 'redux/reducers'

export const PLAYER_SEARCH_1 = 'player1'
export const PLAYER_SEARCH_2 = 'player2'
export const SEARCH_IDS = [PLAYER_SEARCH_1, PLAYER_SEARCH_2]

interface PlayerBarReduxProps
  extends Pick<SearchBarProps, 'suggestions' | 'loading' | 'onFinishedTyping'> {
  resetSearch: () => any
}

interface PlayerBarRequiredProps {
  searchId: string
  onSearchIndividual: (id: string) => void
  resultModalExternalOpts?: { resultsModal: boolean; closeResultsModal: () => void }
}

type H2HSearchBarProps = PlayerBarReduxProps & PlayerBarRequiredProps
const HeadToHeadSearchBar: React.FC<H2HSearchBarProps> = ({
  searchId,
  onSearchIndividual,
  resultModalExternalOpts,
  resetSearch,
  children,
  ...searchProps
}) => {
  const vsPlayerIds = useSelector((state: AppState) => state.h2h.vsPlayerIds)
  const [label, setLabel] = useState<string>('')
  const nameSelected = useRef('')

  const setIndividual = useCallback(
    (id: string, name: string = i18n.t('strData.noPlayerName')) => {
      setLabel(name)
      nameSelected.current = name
      onSearchIndividual(id)
    },
    [onSearchIndividual]
  )

  const handleRenderSuggestion = useCallback(
    (suggestion, { setSearchInput }) => {
      const isAlreadySelected = vsPlayerIds.includes(suggestion.id)

      return (
        <PlayerSuggestion
          player={suggestion}
          className={isAlreadySelected ? styles.suggestionDisabled : undefined}
          onClick={
            isAlreadySelected
              ? undefined
              : () => {
                  setIndividual(suggestion.id, suggestion.fullName)
                  setSearchInput('')
                }
          }
        />
      )
    },
    [setIndividual, vsPlayerIds]
  )

  const handleEnterKey = useCallback(
    (suggestion, { setSearchInput }) => {
      if (suggestion && !vsPlayerIds.includes(suggestion.id)) {
        setIndividual(suggestion?.id, suggestion?.fullName)
        setSearchInput('')
      }
    },
    [setIndividual, vsPlayerIds]
  )

  const handleFocus = useCallback(() => setLabel(''), [])
  const handleBlur = useCallback(() => {
    setLabel(nameSelected.current)
  }, [])

  return (
    <>
      <SearchBar
        {...searchProps}
        minCharsToGetSuggestions={MIN_SEARCH_CHARS}
        viewAllEnabled={false}
        value={label}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onEnterKey={handleEnterKey}
        suggestionType={i18n.t('strSearchBar.player')}
        suggestionTypes={i18n.t('strSearchBar.players')}
        renderSuggestion={handleRenderSuggestion}
        classes={{ suggestionsWrapper: styles.h2hSearchWrapper }}
      >
        {children}
      </SearchBar>
    </>
  )
}

type StateProps = Pick<PlayerBarReduxProps, 'suggestions' | 'loading'>
const mapPlayerBarStateToProps = ({ searchbar }, { searchId }) => ({
  suggestions: searchbar.suggestions[searchId],
  loading: searchbar.suggestionsPending[searchId],
})

type DispatchProps = Pick<PlayerBarReduxProps, 'resetSearch' | 'onFinishedTyping'>
const mapPlayerBarDispatchToProps = (dispatch, ownProps) => ({
  resetSearch: () => dispatch(clearSuggestions(ownProps.searchId)),
  onFinishedTyping: searchWord => dispatch(suggestPlayers(searchWord, ownProps.searchId)),
})

type OwnProps = Omit<H2HSearchBarProps, keyof PlayerBarReduxProps>
export const ConnectedVsSearch = connect<StateProps, DispatchProps, OwnProps, { searchbar: State }>(
  mapPlayerBarStateToProps,
  mapPlayerBarDispatchToProps
)(HeadToHeadSearchBar)

const VsPlayerSearchBar: React.FC = () => {
  const [players, setPlayers] = useState<{ [searchBarId: string]: string }>()
  const [show, showModal, hideModal] = useBooleanState()

  return (
    <MainContainer>
      <div className={styles.h2hPageSearchWrapper}>
        <div className={styles.h2hPageSearch}>
          <div className={styles.h2hPageSearchInner} key={PLAYER_SEARCH_1}>
            <ConnectedVsSearch
              searchId={PLAYER_SEARCH_1}
              onSearchIndividual={id =>
                setPlayers(players => ({ ...players, [PLAYER_SEARCH_1]: id }))
              }
            >
              {' '}
            </ConnectedVsSearch>
          </div>
          <div className={`${styles.vsIconContainer}`}>{i18n.t('strSearchBar.vs')}</div>
          <div className={styles.h2hPageSearchInner} key={PLAYER_SEARCH_2}>
            <ConnectedVsSearch
              searchId={PLAYER_SEARCH_2}
              onSearchIndividual={id =>
                setPlayers(players => ({ ...players, [PLAYER_SEARCH_2]: id }))
              }
            >
              {' '}
            </ConnectedVsSearch>
          </div>
          <SearchButton
            onClick={showModal}
            disabled={
              !(
                players &&
                SEARCH_IDS.every(
                  s => !!players[s] && !(players[PLAYER_SEARCH_1] === players[PLAYER_SEARCH_2])
                )
              )
            }
          />
        </div>

        {!show && (
          <div className={styles.h2hPageNoResults}>
            <p className={styles.noDataText}>{i18n.t('strSearchBar.h2hSearchCompare')}</p>
          </div>
        )}
        {players && show && (
          <div className={styles.h2hPageContent}>
            <HeadToHead
              {...{
                show,
                onClose: hideModal,
                playerIds: [players[PLAYER_SEARCH_1], players[PLAYER_SEARCH_2]],
              }}
            />
          </div>
        )}
      </div>
    </MainContainer>
  )
}

export default VsPlayerSearchBar
